/**
 * Stepthrough (criterion / verdict) body for the NCCN evaluation panel.
 * @module NccnStepthroughBody
 */
import React from "react";
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import { EvaluationItem } from "../../../data-model/nccnGuidelines";
import { CriterionOutcome } from "../analytics/mappers";
import { MedicalNecessityAnalytics } from "../analytics/useMedicalNecessityAnalytics";
import { isHumanMet } from "../utils/bothVerdictsMet";
import {
  FeedbackInput,
  GuidelineReviewInput,
  isAiMet,
} from "../utils/nccnReviewFeedback";
import { CriterionReview } from "./CriterionReview";
import { GuidelineFeedback } from "./GuidelineFeedback";
import { GuidelineTextCard } from "./GuidelineTextCard";

/** Props for {@link NccnStepthroughBody}. */
export interface NccnStepthroughBodyProps {
  guideline: NCCNEvaluationRecord;
  guidelineIndex: number;
  guidelineDisplayPosition: number;
  criteriaIndex: number;
  criteriaCount: number;
  currentItem: EvaluationItem | undefined;
  isSummaryStep: boolean;
  isSubmitting: boolean;
  currentReview: GuidelineReviewInput;
  updateGuidelineReview: (input: GuidelineReviewInput) => void;
  markCorrect: () => void;
  saveFeedback: (input: FeedbackInput) => void;
  analytics: MedicalNecessityAnalytics;
}

/**
 * Renders guideline text plus criterion review or guideline verdict form.
 *
 * @param props - Current guideline/criterion state and analytics callbacks
 * @returns The stepthrough body
 */
export const NccnStepthroughBody: React.FC<NccnStepthroughBodyProps> = ({
  guideline,
  guidelineIndex,
  guidelineDisplayPosition,
  criteriaIndex,
  criteriaCount,
  currentItem,
  isSummaryStep,
  isSubmitting,
  currentReview,
  updateGuidelineReview,
  markCorrect,
  saveFeedback,
  analytics,
}) => (
  <div className="flex min-h-0 flex-1 flex-col gap-[1.25rem] overflow-auto px-[1.25rem] py-[1.125rem]">
    <GuidelineTextCard
      key={guidelineIndex}
      text={guideline.guideline}
      name={guideline.guideline_name}
      aiVerdictMet={isAiMet(guideline)}
      humanVerdictMet={isHumanMet(guideline)}
      histology={guideline.histology}
      category={guideline.category}
      onCopy={() =>
        analytics.trackGuidelineCopyClicked(
          guideline,
          guidelineDisplayPosition,
        )
      }
    />

    {isSummaryStep ? (
      <GuidelineFeedback
        key={`${guidelineIndex}-summary`}
        value={currentReview}
        onChange={(next) => {
          if (next.verdict && next.verdict !== currentReview.verdict) {
            analytics.trackGuidelineVerdictSelected(
              guideline,
              guidelineDisplayPosition,
              next.verdict,
            );
          }
          updateGuidelineReview(next);
        }}
        aiResult={guideline.metadata?.evaluation?.final_result}
        disabled={isSubmitting}
      />
    ) : currentItem ? (
      <CriterionReview
        key={`${guidelineIndex}-${criteriaIndex}`}
        item={currentItem}
        positionLabel={`${Math.min(criteriaIndex + 1, criteriaCount)}/${criteriaCount}`}
        isSaving={isSubmitting}
        guideline={guideline}
        guidelineIndex={guidelineDisplayPosition}
        criterionIndex={criteriaIndex + 1}
        analytics={analytics}
        onMarkCorrect={markCorrect}
        onSaveFeedback={saveFeedback}
        onOutcomeSelected={(outcome: CriterionOutcome) =>
          analytics.trackCriterionOutcomeSelected(
            guideline,
            guidelineDisplayPosition,
            currentItem,
            criteriaIndex + 1,
            outcome,
          )
        }
        onSaveFeedbackClicked={(input) => {
          analytics.trackSaveFeedbackClicked(
            guideline,
            guidelineDisplayPosition,
            currentItem,
            criteriaIndex + 1,
            input,
          );
        }}
      />
    ) : (
      <p className="text-sm text-primaryGray-10">
        No criteria were evaluated for this guideline.
      </p>
    )}
  </div>
);
